import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ConfirmModal, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { librarySuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface LibraryObjectActionsMenuProps {
  objectId: string;
  objectName: string;
  courseCount: number;
  onSuccess?: () => void;
}

function MenuPanel({ open, children, onClose }: { open: boolean; children: ReactNode; onClose: () => void }) {
  if (!open) return null;

  return (
    <div
      className="absolute right-0 bottom-full z-[120] mb-1 min-w-[11rem] overflow-hidden rounded-lg border border-[#c6c5d3] bg-white py-1.5 shadow-[0_8px_24px_rgb(0_0_0_/_0.12)]"
      onClick={onClose}
    >
      {children}
    </div>
  );
}

function MenuLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="block w-full px-4 py-2 text-left text-sm text-[#1a1c1e] hover:bg-[#eeeef0] hover:text-[#142175]">
      {children}
    </Link>
  );
}

function MenuButton({ children, onClick, danger }: { children: ReactNode; onClick?: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      className={`block w-full px-4 py-2 text-left text-sm ${
        danger ? 'text-[#ba1a1a] hover:bg-[#ffdad6] hover:text-[#93000a]' : 'text-[#1a1c1e] hover:bg-[#eeeef0] hover:text-[#142175]'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function LibraryObjectActionsMenu({
  objectId,
  objectName,
  courseCount,
  onSuccess,
}: LibraryObjectActionsMenuProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.BIBLIOTECA_EDITAR);
  const canDelete = hasPermission(PERMISSIONS.BIBLIOTECA_ELIMINAR);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const showUrl = `/library/${objectId}`;
  const previewUrl = `/library/${objectId}/preview`;
  const editUrl = `/library/${objectId}/edit`;
  const inUse = courseCount > 0;

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const removeObject = useMutation({
    mutationFn: () => api(`/library/${objectId}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      setOpen(false);
      toast(librarySuccessToast('delete'));
      queryClient.invalidateQueries({ queryKey: ['library'] });
      onSuccess?.();
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el objeto'));
    },
  });

  return (
    <>
      <div ref={ref} className="relative">
        <button
          type="button"
          className="lib-object-card__menu"
          aria-label={`Acciones para ${objectName}`}
          onClick={() => setOpen((value) => !value)}
        >
          <span className="material-symbols-outlined text-xl" aria-hidden="true">
            more_vert
          </span>
        </button>

        <MenuPanel open={open} onClose={() => setOpen(false)}>
          <MenuLink to={showUrl}>Ver</MenuLink>
          <MenuLink to={previewUrl}>Vista previa</MenuLink>
          {canEdit && <MenuLink to={editUrl}>Editar</MenuLink>}
          {canDelete && inUse ? (
            <span
              className="block px-4 py-2 text-sm text-[#454651]"
              title="No se puede eliminar mientras esté en uso en cursos"
            >
              Eliminar (en uso)
            </span>
          ) : canDelete ? (
            <MenuButton danger onClick={() => setDeleteOpen(true)}>
              Eliminar
            </MenuButton>
          ) : null}
        </MenuPanel>
      </div>

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar objeto"
        description={`¿Está seguro de eliminar el objeto «${objectName}»? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        loading={removeObject.isPending}
        onConfirm={() => removeObject.mutate()}
        onClose={() => {
          if (!removeObject.isPending) setDeleteOpen(false);
        }}
      />
    </>
  );
}
